import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  ButtonActionType,
  Orientation,
  RCSLayout,
  ButtonActionValue,
  IPublishedForm,
} from '../../../modules/templates/interfaces/create-template.interface';
import { EXTENSION, MEDIA_TYPE } from 'src/app/core/constants/common';
import { Icons } from 'src/app/shared/utilities/icons';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-rcs-template-preview',
  templateUrl: './rcs-template-preview.component.html',
  styleUrls: ['./rcs-template-preview.component.scss'],
})
export class RcsTemplatePreviewComponent implements OnChanges {
  @Input() mediaURL?: string;
  @Input() content: string;
  @Input() formData: any = {};
  @Input() ctaButtons?: any[];
  @Input() isCarousel: boolean;
  @Input() carouselCards: any[];
  @Input() isCalledFromCreation = false;
  @Input() activeForm: IPublishedForm;

  icons = Icons;
  mediaType = MEDIA_TYPE;
  buttonActionType = ButtonActionType;
  mediaFileType: string;
  maxLetterCount: number;
  carouselUICards: any[];
  orientation = Orientation;
  rcsLayout = RCSLayout;
  pdfFileName: any;
  title = '';
  buttonActionValue = ButtonActionValue;
  isTextExpanded = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mediaURL']?.currentValue) {
      this.mediaURL = changes['mediaURL'].currentValue;
      if (this.mediaURL && !this.mediaURL.includes('#mediaUrl1#')) {
        this.setTypeAndExtension(this.mediaURL);
      }
    }
    if (changes['formData']?.currentValue) {
      this.formData = changes['formData'].currentValue;
      this.modifyVideoThumbnail(this.formData);
      this.maxLetterCount = 270;

      // Set mediaFileType based on formData.mediaType when form data changes
      if (this.formData?.mediaType) {
        this.mediaFileType = this.formData.mediaType.toUpperCase();

        // Set PDF filename if media type is DOC and media URL exists
        if (
          this.mediaFileType === MEDIA_TYPE.DOC &&
          this.formData.mediaUrl &&
          !this.formData.mediaUrl.includes('#mediaUrl1#')
        ) {
          this.pdfFileName = this.getFileNameFromURL(this.formData.mediaUrl);
        }
      }
    }
    if (changes['ctaButtons']?.currentValue?.length) {
      this.ctaButtons = changes['ctaButtons'].currentValue;
    }
    if (changes['isCarousel']?.currentValue) {
      this.isCarousel = changes['isCarousel'].currentValue;
    }
    if (
      changes['carouselCards']?.currentValue?.length &&
      JSON.stringify(changes['carouselCards'].previousValue) !==
        JSON.stringify(changes['carouselCards']?.currentValue)
    ) {
      this.formData = { ...this.formData, isCarousel: true };
      this.carouselCards = changes['carouselCards'].currentValue;
      this.carouselUICards = [...this.carouselCards];
      if (this.carouselUICards?.length && this.isCalledFromCreation) {
        this.modifyCarouselCards();
      } else if (this.carouselUICards?.length) {
        this.carouselUICards = this.carouselCards.map((card) => {
          return {
            ...card,
            mediaURL: card.mediaURL?.includes('#mediaUrl')
              ? `#${card.mediaURL?.split('/#')?.[1]}`
              : card.mediaURL,
            staticUrl: card.mediaURL?.includes('#mediaUrl')
              ? card.mediaURL?.split('/#')?.[0]
              : '',
            carouselMediaType: card.mediaType,
            cardTitle: card.title,
            ...(card.videoThumbnail && {
              staticThumbnailUrl: card.videoThumbnail?.includes('#thumbnail')
                ? card.videoThumbnail?.split('/#')?.[0]
                : '',
            }),
            ...(card.videoThumbnail && {
              videoThumbnail: card.videoThumbnail?.includes('#thumbnail')
                ? `#${card.videoThumbnail?.split('/#')?.[1]}`
                : card.videoThumbnail,
            }),
          };
        });
      }
    }

    if (!this.isCarousel && this.content) {
      this.content = this.getUpdatedFormNameContent(this.content);
    }
  }

  setTypeAndExtension(mediaURL: string): void {
    const extension = mediaURL.split('.').pop()?.toUpperCase();
    this.mediaFileType =
      extension === EXTENSION.MP4
        ? MEDIA_TYPE.VIDEO
        : extension === EXTENSION.PNG ||
            extension === EXTENSION.JPEG ||
            extension === EXTENSION.JPG ||
            extension === EXTENSION.GIF
          ? MEDIA_TYPE.IMAGE
          : MEDIA_TYPE.DOC;

    if (this.mediaFileType === MEDIA_TYPE.DOC) {
      this.pdfFileName = this.getFileNameFromURL(mediaURL);
    }
  }

  getFileNameFromURL(url: string): string {
    const name = url
      ?.slice(url.lastIndexOf('/') + 1, url.lastIndexOf('_'))
      ?.replaceAll('%20', ' ');
    const arr = url?.split('.');
    const extension = arr[arr.length - 1];
    return `${name}.${extension}`;
  }

  modifyCarouselCards(): void {
    this.carouselUICards = this.carouselCards.map((card) => {
      return {
        ...card,
        mediaURL: card.cardImage || '',
        content: card.cardDesc,
        title: card.cardTitle,
        mediaType: card.cardImage?.includes('.mp4')
          ? MEDIA_TYPE.VIDEO
          : MEDIA_TYPE.IMAGE,
        staticUrl: card.staticUrl,
        videoThumbnail: card.videoThumbnail,
        staticThumbnailUrl: card.staticThumbnailUrl,
        buttons: card.button?.map((cta: any) => {
          return {
            ...cta,
            buttonAction: cta.action?.replace(/_/g, ' '),
          };
        }),
      };
    });
  }

  modifyVideoThumbnail(formData: any): void {
    if (!this.isCalledFromCreation) {
      this.formData = {
        ...formData,
        isButton: formData?.rcsTemplates?.[0]?.buttons?.length ? true : false,
        buttonRequests: formData?.rcsTemplates?.[0]?.buttons,
        ...formData?.rcsTemplates?.[0],
        mediaUrl: formData?.rcsTemplates?.[0]?.mediaURL,
        mediaType: formData?.rcsTemplates?.[0]?.mediaType?.toUpperCase(),
      };
      delete this.formData?.rcsTemplates;
    } else {
      if (this.formData?.videoThumbnail?.includes('#thumbnail1#')) {
        if (this.formData?.staticThumbnailUrl?.length) {
          this.formData = {
            ...formData,
            videoThumbnail: this.formData?.videoThumbnail,
          };
        } else {
          this.formData = {
            ...formData,
            videoThumbnail: '',
          };
        }
      }
    }
  }

  toggleText(): void {
    this.isTextExpanded = !this.isTextExpanded;
  }

  getUpdatedFormNameContent(content: string | undefined): string {
    if (!content) {
      return '';
    }
    const { formName } = this.activeForm || {};
    const formCode = this.activeForm?.formCode;

    if (formName && formCode) {
      const formUrl = `<a href="${environment.FORM_PREVIEW_URL}/form/${formCode}/PREVIEW" target="_blank">${formName} </a>`;
      return content
        .replace('#form_url#', formUrl)
        .replace('#form_url_tiny#', formUrl);
    } else {
      return content;
    }
  }

  getButtonUrl(button: any, action: string): string | undefined {
    const prefixes = ['http://', 'https://'];
    let url: string | undefined;

    switch (action) {
      case 'STATIC CTA':
      case this.buttonActionType.STATIC_CTA: {
        url = (button?.url || button?.buttonValue)?.trim();
        if (url && !prefixes.some((prefix) => url?.startsWith(prefix))) {
          url = `https://${url}`;
        }
        break;
      }
      case 'DYNAMIC CTA':
      case this.buttonActionType.DYNAMIC_CTA: {
        url = button?.shortenerProvider ? button.baseUrl : button?.url;
        if (url && !prefixes.some((prefix) => url?.startsWith(prefix))) {
          url = `https://${url}`;
        }
        break;
      }
      case 'WEB FORM URL':
      case this.buttonActionType.WEB_FORM_URL: {
        const formCode = button?.buttonValue || button?.formCode;
        if (formCode) {
          url = `${environment.FORM_PREVIEW_URL}/form/${formCode}/PREVIEW/`;
        }
        break;
      }
    }
    return url;
  }

  openButtonAction(button: any): void {
    const url = this.getButtonUrl(button, button?.buttonAction.toUpperCase());
    if (url) {
      window.open(url, '_blank');
    }
  }
}